
import React, { useEffect, useMemo, useState } from 'react';
import { Download, FileSpreadsheet, CheckCircle, X, Layout, Calendar, User, Image as ImageIcon, ChevronDown, ChevronRight, ZoomIn, Folder, ArrowLeft, CheckSquare, Square, CloudUpload } from 'lucide-react';
import { SubmissionData, Campaign, Aggregator, Brand } from '../../../types';
import { SubmissionStatus, AdminView, WorkflowStage } from '../../../enums';
import { apiClient } from '../../../services/apiClient';

interface ReportsViewProps {
    submissions: SubmissionData[];
    campaigns: Campaign[];
    onUpdateSubmissions: (submissions: SubmissionData[]) => void;
    onAddNotification: (type: 'info' | 'success' | 'warning' | 'error', title: string, message: string, targetView?: AdminView, targetId?: string) => void;
    showToast?: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
    aggregators?: Aggregator[];
    brands?: Brand[];
}

const AVAILABLE_COLUMNS = [
    { id: 'subId', label: 'Submission ID' },
    { id: 'date', label: 'Submission Date' },
    { id: 'manager', label: 'Manager Name' },
    { id: 'brand', label: 'Brand' },
    { id: 'name', label: 'Item Name' },
    { id: 'newPrice', label: 'Original Price' },
    { id: 'desc', label: 'Description' },
    { id: 'platforms', label: 'Target Platforms' },
    { id: 'createdAt', label: 'Created On' },
    { id: 'startDate', label: 'Start Date' },
    { id: 'endDate', label: 'End Date' },
    { id: 'daysRunning', label: 'Days Running' },
    { id: 'daysRemaining', label: 'Days Left' },
    { id: 'duration', label: 'Duration' },
    { id: 'discountType', label: 'Discount Type' },
    { id: 'discountValue', label: 'Discount Value' },
    { id: 'discountDisplay', label: 'Discount Display' },
    { id: 'fundingType', label: 'Funding Type' },
    { id: 'fundingDisplay', label: 'Funding Display' },
    { id: 'sterlingShare', label: 'Sterling Share' },
    { id: 'cofundShare', label: 'Co-Funded Share' },
];

export const ReportsView: React.FC<ReportsViewProps> = ({ submissions, campaigns, onUpdateSubmissions, onAddNotification, showToast, aggregators = [], brands = [] }) => {
  const ALL_CAMPAIGNS = 'ALL_CAMPAIGNS';
  const [selectedAggregator, setSelectedAggregator] = useState<string>('All Platforms');
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>(campaigns[0]?.id || ALL_CAMPAIGNS);
  const [isMarking, setIsMarking] = useState(false);
  const [brandFilter, setBrandFilter] = useState<string[]>([]);

  const brandLookup = useMemo(() => {
    const map = new Map<string, string>();
    brands.forEach((b) => map.set(b.id, b.name));
    return map;
  }, [brands]);
  const brandNameSet = useMemo(() => new Set(brandFilter.map((id) => (brandLookup.get(id) || '').toLowerCase()).filter(Boolean)), [brandFilter, brandLookup]);
  const applyBrandFilter = brandFilter.length > 0;

  const scopedCampaigns = useMemo(() => {
    if (!applyBrandFilter) return campaigns;
    return campaigns.filter((c) => {
      if ((c as any).scope === 'ALL') return true;
      const ids = (c as any).brandIds || [];
      if (!Array.isArray(ids) || ids.length === 0) return false;
      return ids.some((id: string) => brandFilter.includes(id));
    });
  }, [applyBrandFilter, brandFilter, campaigns]);

  // Keep selection in sync if campaigns load later or brand filter changes
  React.useEffect(() => {
    if (!scopedCampaigns.length) {
      setSelectedCampaignId(ALL_CAMPAIGNS);
      return;
    }
    // If the selected campaign no longer exists, fall back to first
    const exists = scopedCampaigns.some(c => c.id === selectedCampaignId);
    if (!exists && selectedCampaignId !== ALL_CAMPAIGNS) {
      setSelectedCampaignId(scopedCampaigns[0].id || ALL_CAMPAIGNS);
    }
  }, [scopedCampaigns, selectedCampaignId, ALL_CAMPAIGNS]);
  
  // Excel Export State
  const [showExportModal, setShowExportModal] = useState(false);
  // Default selection keeps legacy columns checked; new discount/funding columns remain available but unchecked
  // Persist column preferences per user/device (localStorage)
  const STORAGE_KEY = 'reports-export-columns';
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set(['brand', 'name', 'newPrice', 'desc', 'subId']);
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return new Set(parsed);
      }
    } catch (e) {
      // ignore parse errors and fall back to defaults
    }
    return new Set(['brand', 'name', 'newPrice', 'desc', 'subId']);
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(selectedColumns)));
    } catch (e) {
      // ignore storage errors
    }
  }, [selectedColumns]);

  // Image Export State
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageBrands, setSelectedImageBrands] = useState<Set<string>>(new Set());
  const [openBrandFolder, setOpenBrandFolder] = useState<string | null>(null); // For drill-down navigation
  const [openSubmissionId, setOpenSubmissionId] = useState<string | null>(null); // second-level drill-down
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState<Set<string>>(new Set());
  const [previewImage, setPreviewImage] = useState<{url: string, title: string} | null>(null);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [downloadScope, setDownloadScope] = useState<{ brand?: string; submissionId?: string; submissionIds?: string[] } | null>(null);
  const [includeExcelWithImages, setIncludeExcelWithImages] = useState(true);

  // Flatten items from all Approved submissions for preview & export
  const resolveImageSrc = (raw?: string | null) => {
    if (!raw) return null;
    const toProxy = (key: string) => `/api/assets?key=${encodeURIComponent(key)}`;
    if (raw.startsWith('data:')) return raw;
        if (raw.startsWith('/api/assets')) return raw;
    if (raw.includes('.r2.dev/')) return toProxy(raw.split('.r2.dev/')[1]);
    if (raw.includes('sterlingops-assets/')) return toProxy(raw.split('sterlingops-assets/')[1]);
    if (raw.startsWith('uploads/')) return toProxy(raw);
    if (raw.startsWith('http')) return raw;
    return `data:image/png;base64,${raw}`;
  };

  const allApprovedItems = useMemo(() => submissions
    .filter(s => s.status === SubmissionStatus.APPROVED && s.type !== 'Proposal')
    .filter(s => {
      if (!applyBrandFilter) return true;
      const brandsList = s.brands || [];
      if (!brandsList.length) return false;
      return brandsList.some((b) => brandNameSet.has(String(b).toLowerCase()));
    })
    .flatMap(s => {
      const campaign = campaigns.find(c => c.id === s.campaignId) as any || {};
      return s.items.map(i => ({
        ...i, 
        brandLabel: s.brands.join(', '),
        brand: s.brands.join(', '),
        brandsList: s.brands,
        submissionId: s.submissionId,
        manager: s.manager.name,
        date: s.timestamp,
        campaignId: s.campaignId,
        campaignName: s.campaignName,
        campaignDiscountType: campaign.discountType || '',
        campaignDiscountValue: campaign.discountValue ?? '',
        campaignDiscountDisplay: campaign.discountDisplay || '',
        campaignFundingType: campaign.fundingType || '',
        campaignFundingDisplay: campaign.fundingDisplay || '',
        campaignSterlingShare: campaign.sterlingDiscountShareValue ?? '',
        campaignCofundShare: campaign.cofundedDiscountShareValue ?? '',
        createdAt: campaign.createdAt || '',
        startDate: campaign.startDate || '',
        endDate: campaign.endDate || '',
        daysRunning: campaign.daysRunning ?? '',
        daysRemaining: campaign.daysRemaining ?? '',
        duration: campaign.totalDurationDays ?? '',
        image: resolveImageSrc((i as any).image || (i as any).imagePreview || null)
      }))
    }), [submissions, campaigns, applyBrandFilter, brandNameSet]);

  const campaignFilteredItems = useMemo(() => {
      if (selectedCampaignId === ALL_CAMPAIGNS || !selectedCampaignId) return allApprovedItems;
      return allApprovedItems.filter(i => i.campaignId === selectedCampaignId);
  }, [allApprovedItems, selectedCampaignId, ALL_CAMPAIGNS]);

  const previewItems = selectedAggregator === 'All Platforms'
    ? campaignFilteredItems
    : campaignFilteredItems.filter(i => i.selectedAggregators.includes(selectedAggregator));

  // --- EXCEL LOGIC ---
  const toggleColumn = (colId: string) => {
      const newSet = new Set(selectedColumns);
      if (newSet.has(colId)) newSet.delete(colId);
      else newSet.add(colId);
      setSelectedColumns(newSet);
  };

  const handleDownloadExcel = async () => {
    if (previewItems.length === 0) {
        if(showToast) showToast('warning', 'No Data', 'There are no approved items to export for this selection.');
        return;
    }

    const ExcelJS = (window as any).ExcelJS;
    if (!ExcelJS) {
        if(showToast) showToast('error', 'Library Error', 'Excel generator missing. Please refresh.');
        return;
    }

    try {
        if(showToast) showToast('info', 'Exporting', `Generating Excel for ${selectedAggregator}...`);

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet(`${selectedAggregator === 'All Platforms' ? 'All' : selectedAggregator} Export`);

        const activeColumns = AVAILABLE_COLUMNS.filter(col => selectedColumns.has(col.id));
        sheet.columns = activeColumns.map(col => ({ 
            header: col.label, 
            key: col.id, 
            width: col.id === 'desc' ? 40 : 20 
        }));

        const headerRow = sheet.getRow(1);
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } }; // Teal 700
        headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
        headerRow.height = 30;

        previewItems.forEach(item => {
            const rowData: any = {};
            if (selectedColumns.has('subId')) rowData.subId = item.submissionId;
            if (selectedColumns.has('date')) rowData.date = item.date;
            if (selectedColumns.has('manager')) rowData.manager = item.manager;
            if (selectedColumns.has('brand')) {
                const brandsJoined = (item as any).brandsList ? (item as any).brandsList.join(', ') : '';
                rowData.brand = (item as any).brandLabel || brandsJoined || (item as any).brand;
            }
            if (selectedColumns.has('name')) rowData.name = item.name;
            if (selectedColumns.has('newPrice')) rowData.newPrice = parseFloat(item.sellingPrice) || 0;
            if (selectedColumns.has('desc')) rowData.desc = item.description;
            if (selectedColumns.has('platforms')) rowData.platforms = item.selectedAggregators.join(', ');
            if (selectedColumns.has('createdAt')) rowData.createdAt = (item as any).createdAt || '';
            if (selectedColumns.has('startDate')) rowData.startDate = (item as any).startDate || '';
            if (selectedColumns.has('endDate')) rowData.endDate = (item as any).endDate || '';
            if (selectedColumns.has('daysRunning')) rowData.daysRunning = (item as any).daysRunning ?? '';
            if (selectedColumns.has('daysRemaining')) rowData.daysRemaining = (item as any).daysRemaining ?? '';
            if (selectedColumns.has('duration')) rowData.duration = (item as any).duration ?? '';
            if (selectedColumns.has('discountType')) rowData.discountType = (item as any).campaignDiscountType || '';
            if (selectedColumns.has('discountValue')) rowData.discountValue = (item as any).campaignDiscountValue ?? '';
            if (selectedColumns.has('discountDisplay')) rowData.discountDisplay = (item as any).campaignDiscountDisplay || '';
            if (selectedColumns.has('fundingType')) rowData.fundingType = (item as any).campaignFundingType || '';
            if (selectedColumns.has('fundingDisplay')) rowData.fundingDisplay = (item as any).campaignFundingDisplay || '';
            if (selectedColumns.has('sterlingShare')) rowData.sterlingShare = (item as any).campaignSterlingShare ?? '';
            if (selectedColumns.has('cofundShare')) rowData.cofundShare = (item as any).campaignCofundShare ?? '';
            if (selectedColumns.has('discountType')) rowData.discountType = (item as any).campaignDiscountType || '';
            if (selectedColumns.has('discountValue')) rowData.discountValue = (item as any).campaignDiscountValue ?? '';
            if (selectedColumns.has('discountDisplay')) rowData.discountDisplay = (item as any).campaignDiscountDisplay || '';
            if (selectedColumns.has('fundingType')) rowData.fundingType = (item as any).campaignFundingType || '';
            if (selectedColumns.has('fundingDisplay')) rowData.fundingDisplay = (item as any).campaignFundingDisplay || '';
            if (selectedColumns.has('sterlingShare')) rowData.sterlingShare = (item as any).campaignSterlingShare ?? '';
            if (selectedColumns.has('cofundShare')) rowData.cofundShare = (item as any).campaignCofundShare ?? '';

            const row = sheet.addRow(rowData);

            if (selectedColumns.has('newPrice')) {
                row.getCell('newPrice').numFmt = '#,##0.00';
                row.getCell('newPrice').alignment = { horizontal: 'right' };
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Sterling_${selectedAggregator.replace(' ', '_')}_Export_${new Date().toISOString().slice(0,10)}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);

        if(showToast) showToast('success', 'Export Complete', 'The Excel file has been downloaded.');
        setShowExportModal(false);
    } catch (e) {
        console.error(e);
        if(showToast) showToast('error', 'Export Failed', 'An unexpected error occurred during export.');
    }
  };

  const handleMarkExported = async () => {
      const affectedSubmissionIds = new Set(previewItems.map(i => i.submissionId));
      if (affectedSubmissionIds.size === 0) {
          if(showToast) showToast('warning', 'No Items', 'No approved items available to mark as exported.');
          return;
      }

      setIsMarking(true);
      try {
        await Promise.all(
          Array.from(affectedSubmissionIds).map((id) =>
            apiClient.submissions.updateStatus(id, {
              status: SubmissionStatus.EXPORTED,
              currentStage: WorkflowStage.COMPLETED,
              workflowLog: {
                stage: WorkflowStage.COMPLETED,
                action: 'Approve',
                comment: 'Marked as exported',
                user: 'System',
                timestamp: new Date().toISOString(),
              },
            })
          )
        );
        const refreshed = await apiClient.submissions.list().catch(() => null);
        const items = Array.isArray((refreshed as any)?.items)
          ? (refreshed as any).items
          : Array.isArray(refreshed)
          ? refreshed
          : [];
        if (items.length) {
          onUpdateSubmissions(items);
        } else {
          const updatedSubmissions = submissions.map(s => {
              if (affectedSubmissionIds.has(s.submissionId) && s.status === SubmissionStatus.APPROVED) {
                  return { ...s, status: SubmissionStatus.EXPORTED };
              }
              return s;
          });
          onUpdateSubmissions(updatedSubmissions);
        }
        onAddNotification('success', 'Marked Exported', `Flagged ${affectedSubmissionIds.size} submissions.`, AdminView.REPORTS);
        if(showToast) showToast('success', 'Status Updated', `Marked ${affectedSubmissionIds.size} submissions as Exported.`);
      } catch (error) {
        console.error(error);
        if(showToast) showToast('error', 'Update Failed', 'Could not mark submissions as exported.');
      } finally {
        setIsMarking(false);
      }
  };

  // --- IMAGE LOGIC ---
  const imagesByBrand = previewItems.reduce((acc, item: any) => {
      if (!item.image) return acc;
      const brands = Array.isArray(item.brandsList) && item.brandsList.length ? item.brandsList : [item.brandLabel || 'Misc'];
      brands.forEach((b: string) => {
        if (!acc[b]) acc[b] = [];
        acc[b].push(item);
      });
      return acc;
  }, {} as Record<string, typeof previewItems>);

  const brandsWithImages = Object.keys(imagesByBrand);

  const toggleImageBrandSelect = (brand: string, e?: React.MouseEvent) => {
      if(e) e.stopPropagation();
      const newSet = new Set(selectedImageBrands);
      if (newSet.has(brand)) newSet.delete(brand);
      else newSet.add(brand);
      setSelectedImageBrands(newSet);
  };

  const selectAllImageBrands = () => {
      setSelectedImageBrands(new Set(brandsWithImages));
  };
  
  const clearImageBrands = () => {
      setSelectedImageBrands(new Set());
  };

  const openDownloadModal = () => {
      // Decide scope based on current context and selections
      if (openBrandFolder) {
          if (openSubmissionId) {
              setDownloadScope({ brand: openBrandFolder, submissionId: openSubmissionId });
          } else if (selectedSubmissionIds.size > 0) {
              setDownloadScope({ brand: openBrandFolder, submissionIds: Array.from(selectedSubmissionIds) });
          } else {
              setDownloadScope({ brand: openBrandFolder });
          }
      } else {
          setDownloadScope(null); // brand-level selection already handled by selectedImageBrands
      }
      setShowDownloadConfirm(true);
  };

  const handleDownloadImages = async (withExcel: boolean, scope?: { brand?: string; submissionId?: string; submissionIds?: string[] }) => {
      const scopedBrands = scope?.brand ? [scope.brand] : Array.from(selectedImageBrands);
      if (scopedBrands.length === 0) return;

      const JSZip = (window as any).JSZip;
      if (!JSZip) {
          if(showToast) showToast('error', 'Library Missing', 'JSZip library not loaded.');
          return;
      }

      if(showToast) showToast('info', 'Zipping Files', 'Compressing images, please wait...');

      const zip = new JSZip();
      let count = 0;

      // Collect items by submission for optional Excel export
      const submissionBuckets: Record<string, any[]> = {};

      for (const brand of scopedBrands) {
          const brandFolderName = brand || 'Misc';
          const brandFolderSafe = brandFolderName.replace(/[^a-z0-9\-_\s]/gi, '_');
          const brandFolder = zip.folder(brandFolderSafe);
          const items = (imagesByBrand[brand] || []).filter(it => {
            const idStr = String(it.submissionId);
            if (scope?.submissionId) return idStr === scope.submissionId;
            if (scope?.submissionIds && scope.submissionIds.length) return scope.submissionIds.includes(idStr);
            return true;
          });
          for (const item of items) {
              const imageSrc = resolveImageSrc(item.image || (item as any).imagePreview);
              if (!imageSrc) continue;
              const ext = item.imageName ? item.imageName.split('.').pop() || 'jpg' : 'jpg';
              const safeName = item.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
              const submissionFolder = brandFolder?.folder(`Submission_${String(item.submissionId).replace(/[^a-z0-9\-_.]/gi, '_')}`);
              if (withExcel) {
                const key = String(item.submissionId);
                if (!submissionBuckets[key]) submissionBuckets[key] = [];
                submissionBuckets[key].push(item);
              }

              const shouldFetch = imageSrc.startsWith('http') || imageSrc.startsWith('/');
              if (shouldFetch) {
                  try {
                      const resp = await fetch(imageSrc);
                      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                      const blob = await resp.arrayBuffer();
                      submissionFolder?.file(`${safeName}.${ext}`, blob);
                      count++;
                  } catch (err) {
                      console.warn('Failed to fetch image for zip', imageSrc, err);
                  }
              } else {
                  const base64Data = imageSrc.split(',')[1];
                  if (base64Data) {
                    submissionFolder?.file(`${safeName}.${ext}`, base64Data, { base64: true });
                    count++;
                  } else {
                    console.warn('Skipping image with invalid data URL', imageSrc);
                  }
              }
          }
      }

      // Optional Excel per submission
      if (withExcel) {
        const ExcelJS = (window as any).ExcelJS;
        if (ExcelJS) {
          const dataFolder = zip.folder('data');
          for (const [submissionId, items] of Object.entries(submissionBuckets)) {
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet(`Submission ${submissionId}`);
            sheet.columns = [
              { header: 'Item Name', key: 'name', width: 30 },
              { header: 'Original Price', key: 'price', width: 15 },
              { header: 'Description', key: 'desc', width: 40 },
              { header: 'Platforms', key: 'plats', width: 25 },
              { header: 'Brands', key: 'brands', width: 25 },
            ];
            items.forEach((it: any) => {
              sheet.addRow({
                name: it.name,
                price: parseFloat(it.sellingPrice) || 0,
                desc: it.description || '',
                plats: Array.isArray(it.selectedAggregators) ? it.selectedAggregators.join(', ') : '',
                brands: (it.brandsList || []).join(', '),
              });
            });
            try {
              const buffer = await workbook.xlsx.writeBuffer();
              dataFolder?.file(`Submission_${submissionId}.xlsx`, buffer);
            } catch (err) {
              console.warn('Failed to generate Excel for submission', submissionId, err);
            }
          }
        } else {
          console.warn('ExcelJS not available; skipping Excel attachments');
        }
      }

      try {
          const content = await zip.generateAsync({ type: "blob" });
          const url = window.URL.createObjectURL(content);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Sterling_Images_${new Date().toISOString().slice(0,10)}.zip`;
          link.click();
          window.URL.revokeObjectURL(url);
          
          if(showToast) showToast('success', 'Download Ready', `Successfully zipped ${count} images.`);
          setShowImageModal(false);
          setShowDownloadConfirm(false);
      } catch (e) {
          console.error(e);
          if(showToast) showToast('error', 'Zip Failed', 'Could not create the zip file.');
      }
  };


  return (
    <div className="animate-fade-in space-y-6 relative">
        {/* LIGHTBOX MODAL */}
        {previewImage && (
            <div 
                className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 animate-fade-in"
                onClick={() => setPreviewImage(null)}
            >
                <div className="relative max-w-5xl w-full max-h-screen flex flex-col items-center">
                    <img 
                        src={previewImage.url} 
                        alt={previewImage.title} 
                        className="max-h-[85vh] w-auto rounded-lg shadow-2xl"
                    />
                    <div className="mt-4 text-white font-bold text-lg tracking-wide">{previewImage.title}</div>
                    <button 
                        onClick={() => setPreviewImage(null)}
                        className="absolute -top-12 right-0 p-2 text-white/50 hover:text-white transition-colors"
                    >
                        <X className="w-8 h-8" />
                    </button>
                </div>
            </div>
        )}

        {/* IMAGE DOWNLOAD MODAL - FILE MANAGER STYLE */}
        {showImageModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/50 backdrop-blur-sm animate-fade-in">
                <div className="bg-zinc-50 rounded-3xl shadow-2xl max-w-4xl w-full flex flex-col h-[80vh] overflow-hidden border border-zinc-200">
                     
                     {/* Header */}
                     <div className="px-8 py-5 border-b border-zinc-200 flex justify-between items-center bg-white shrink-0">
                        <div>
                            <h3 className="text-xl font-extrabold text-zinc-900 flex items-center gap-2">
                                <ImageIcon className="w-6 h-6 text-teal-600" />
                                Image Manager
                            </h3>
                            <p className="text-sm text-zinc-500 font-medium mt-1">
                                {openBrandFolder 
                                    ? `Viewing images in "${openBrandFolder}"` 
                                    : "Select brand folders to download"
                                }
                            </p>
                        </div>
                        <button onClick={() => setShowImageModal(false)} className="p-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-400 hover:text-zinc-600">
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Toolbar */}
                    <div className="bg-zinc-100/50 border-b border-zinc-200 px-8 py-3 flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-3">
                            {openBrandFolder ? (
                                <button 
                                    onClick={() => { setOpenBrandFolder(null); setOpenSubmissionId(null); }}
                                    className="flex items-center gap-1 text-sm font-bold text-zinc-600 hover:text-teal-600 transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Back to Folders
                                </button>
                            ) : (
                                <>
                                    <button onClick={selectAllImageBrands} className="text-xs font-bold text-teal-600 hover:bg-teal-50 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2">
                                        <CheckSquare className="w-4 h-4" /> Select All
                                    </button>
                                    <div className="w-px h-4 bg-zinc-300"></div>
                                    <button onClick={clearImageBrands} className="text-xs font-bold text-zinc-500 hover:bg-zinc-200 hover:text-zinc-700 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2">
                                        <Square className="w-4 h-4" /> Clear
                                    </button>
                                </>
                            )}
                        </div>
                        
                        {!openBrandFolder && (
                            <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">
                                Select brand folders to download ({selectedImageBrands.size} selected)
                            </div>
                        )}
                    </div>

                    {/* Content Area - File Browser */}
                    <div className="p-8 overflow-y-auto flex-1 custom-scrollbar bg-white">
                         {brandsWithImages.length === 0 ? (
                             <div className="h-full flex flex-col items-center justify-center text-zinc-400 opacity-60">
                                 <Folder className="w-24 h-24 mb-4 text-zinc-200" />
                                 <p className="font-bold text-lg">No Images Available</p>
                                 <p className="text-sm">Approve submissions with images to see them here.</p>
                             </div>
                         ) : (
                             <>
                                {openBrandFolder ? (() => {
                                    const items = imagesByBrand[openBrandFolder] || [];
                                    const submissionsMap: Record<string, any[]> = {};
                                    items.forEach((it: any) => {
                                      const key = String(it.submissionId || 'unknown');
                                      if (!submissionsMap[key]) submissionsMap[key] = [];
                                      submissionsMap[key].push(it);
                                    });
                                    const submissionIds = Object.keys(submissionsMap);
                                     if (!openSubmissionId) {
                                      return (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 animate-fade-in">
                                          {submissionIds.map((subId) => {
                                            const count = submissionsMap[subId].length;
                                            const isSubSelected = selectedSubmissionIds.has(subId);
                                            return (
                                              <div
                                                key={subId}
                                                className="relative group p-4 rounded-2xl border-2 transition-all cursor-pointer flex flex-col items-center text-center gap-3 bg-zinc-50 border-transparent hover:border-zinc-200 hover:bg-white hover:shadow-lg hover:-translate-y-1"
                                                onClick={() => setOpenSubmissionId(subId)}
                                              >
                                                <div 
                                                  className="absolute top-3 right-3 z-10"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    const next = new Set(selectedSubmissionIds);
                                                    if (next.has(subId)) next.delete(subId); else next.add(subId);
                                                    setSelectedSubmissionIds(next);
                                                  }}
                                                >
                                                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${isSubSelected ? 'bg-teal-600 border-teal-600' : 'bg-white border-zinc-300 group-hover:border-teal-300'}`}>
                                                      {isSubSelected && <CheckSquare className="w-3 h-3 text-white" />}
                                                  </div>
                                                </div>
                                                <div className="w-14 h-14 rounded-2xl bg-teal-100 text-teal-600 flex items-center justify-center font-extrabold">
                                                  {count}
                                                </div>
                                                <div>
                                                  <h4 className="font-bold text-sm mb-1 text-zinc-800">Submission {subId}</h4>
                                                  <span className="inline-block px-2 py-0.5 rounded-md bg-white border border-zinc-200 text-[10px] font-bold text-zinc-400">
                                                    {count} images
                                                  </span>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    }
                                    const subItems = submissionsMap[openSubmissionId] || [];
                                    return (
                                      <>
                                        <div className="flex items-center gap-3 mb-4">
                                          <button 
                                            onClick={() => setOpenSubmissionId(null)} 
                                            className="text-sm font-bold text-zinc-600 hover:text-teal-600 flex items-center gap-2"
                                          >
                                            <ArrowLeft className="w-4 h-4" /> Back to Submission list
                                          </button>
                                          <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Submission {openSubmissionId}</span>
                                          <button
                                            onClick={() => { setOpenSubmissionId(null); }}
                                            className="ml-auto px-3 py-2 text-xs font-bold bg-white text-zinc-700 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors shadow-sm"
                                          >
                                            Back to submissions
                                          </button>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 animate-fade-in">
                                          {subItems.map((item: any, idx: number) => (
                                            <div 
                                                key={idx} 
                                                className="group aspect-square rounded-2xl overflow-hidden bg-zinc-100 border border-zinc-200 cursor-zoom-in relative shadow-sm hover:shadow-md transition-all"
                                                onClick={() => setPreviewImage({ url: item.image, title: item.name })}
                                            >
                                                <img 
                                                   src={item.image} 
                                                   alt={item.name} 
                                                   className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                />
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                                                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md" />
                                                </div>
                                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <p className="text-white text-xs font-bold truncate">{item.name}</p>
                                                </div>
                                            </div>
                                          ))}
                                        </div>
                                      </>
                                    );
                                })() : (
                                    // ROOT VIEW: Grid of Folders
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 animate-fade-in">
                                    {brandsWithImages.map(brand => {
                                            const isSelected = selectedImageBrands.has(brand);
                                            const count = imagesByBrand[brand].length;

                                            return (
                                                <div 
                                                    key={brand}
                                                    onClick={() => { setOpenBrandFolder(brand); setOpenSubmissionId(null); setSelectedSubmissionIds(new Set()); }}
                                                    className={`
                                                        relative group p-4 rounded-2xl border-2 transition-all cursor-pointer flex flex-col items-center text-center gap-3 hover:shadow-lg hover:-translate-y-1
                                                        ${isSelected 
                                                            ? 'bg-teal-50 border-teal-200 shadow-teal-100' 
                                                            : 'bg-zinc-50 border-transparent hover:border-zinc-200 hover:bg-white'
                                                        }
                                                    `}
                                                >
                                                    {/* Selection Checkbox (Intercept click) */}
                                                    <div 
                                                        className="absolute top-3 right-3 z-10"
                                                        onClick={(e) => toggleImageBrandSelect(brand, e)}
                                                    >
                                                        <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-teal-600 border-teal-600' : 'bg-white border-zinc-300 group-hover:border-teal-300'}`}>
                                                            {isSelected && <CheckSquare className="w-3.5 h-3.5 text-white" />}
                                                        </div>
                                                    </div>

                                                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-colors ${isSelected ? 'bg-teal-200 text-teal-700' : 'bg-teal-100 text-teal-400 group-hover:bg-teal-200 group-hover:text-teal-600'}`}>
                                                        <Folder className="w-8 h-8 fill-current" />
                                                    </div>
                                                    
                                                    <div>
                                                        <h4 className={`font-bold text-sm mb-1 ${isSelected ? 'text-teal-900' : 'text-zinc-700'}`}>{brand}</h4>
                                                        <span className="inline-block px-2 py-0.5 rounded-md bg-white border border-zinc-200 text-[10px] font-bold text-zinc-400">
                                                            {count} items
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                         )}
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-zinc-200 bg-zinc-50/80 shrink-0 flex gap-4 items-center">
                         <div className="flex-1">
                             {selectedImageBrands.size > 0 && (
                                 <p className="text-xs font-bold text-teal-600 animate-fade-in">
                                     Ready to download {selectedImageBrands.size} folders as ZIP
                                 </p>
                             )}
                         </div>
                         <button 
                            onClick={() => setShowImageModal(false)}
                            className="px-6 py-3 bg-white border border-zinc-200 text-zinc-600 font-bold rounded-xl hover:bg-zinc-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={openDownloadModal}
                            disabled={selectedImageBrands.size === 0}
                            className={`
                                px-8 py-3 text-white font-bold rounded-xl shadow-lg transition-all flex items-center gap-2
                                ${selectedImageBrands.size === 0 
                                    ? 'bg-zinc-300 cursor-not-allowed shadow-none text-zinc-500' 
                                    : 'bg-teal-600 hover:bg-teal-700 shadow-teal-200 active:scale-95'
                                }
                            `}
                        >
                            <Download className="w-4 h-4" />
                            {selectedImageBrands.size > 0 ? `Download (${selectedImageBrands.size})` : 'Download Selected'}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* EXPORT EXCEL MODAL */}
        {showExportModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/50 backdrop-blur-sm animate-fade-in">
                <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
                    <div className="px-8 py-6 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/50">
                        <div>
                            <h3 className="text-xl font-bold text-zinc-900">Export Excel</h3>
                            <p className="text-sm text-zinc-500 font-medium">Customize your data file</p>
                        </div>
                        <button onClick={() => setShowExportModal(false)} className="p-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-400 hover:text-zinc-600">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    
                    <div className="p-8">
                        <div className="mb-6 bg-teal-50 p-4 rounded-xl flex items-center gap-3 text-teal-800 font-medium border border-teal-100">
                            <FileSpreadsheet className="w-5 h-5" />
                            <span>Exporting for: <span className="font-bold">{selectedAggregator}</span></span>
                        </div>

                        <div className="mb-2 flex items-center justify-between">
                            <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Select Columns</h4>
                            <button 
                                onClick={() => setSelectedColumns(new Set(AVAILABLE_COLUMNS.map(c => c.id)))}
                                className="text-xs font-bold text-teal-600 hover:text-teal-700"
                            >
                                Select All
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-8">
                            {AVAILABLE_COLUMNS.map((col) => {
                                const isSelected = selectedColumns.has(col.id);
                                return (
                                    <button
                                        key={col.id}
                                        onClick={() => toggleColumn(col.id)}
                                        className={`flex items-center gap-3 p-3 rounded-xl border text-sm font-bold transition-all text-left
                                            ${isSelected 
                                                ? 'bg-zinc-900 border-zinc-900 text-white shadow-md' 
                                                : 'bg-white border-zinc-200 text-zinc-500 hover:border-zinc-300'
                                            }
                                        `}
                                    >
                                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'border-white bg-white/20' : 'border-zinc-300'}`}>
                                            {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                                        </div>
                                        {col.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex gap-3">
                            <button 
                                onClick={() => setShowExportModal(false)}
                                className="flex-1 py-3 bg-white border border-zinc-200 text-zinc-600 font-bold rounded-xl hover:bg-zinc-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleDownloadExcel}
                                className="flex-1 py-3 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 shadow-lg shadow-emerald-200 transition-all active:scale-95 flex items-center justify-center gap-2"
                            >
                                <Download className="w-4 h-4" />
                                Export Excel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* DOWNLOAD CONFIRM MODAL */}
        {showDownloadConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/50 backdrop-blur-sm animate-fade-in">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                    <div className="px-6 py-4 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-bold text-zinc-900">Prepare Download</h3>
                            <p className="text-sm text-zinc-500">Brand folders + submission subfolders.</p>
                        </div>
                        <button onClick={() => setShowDownloadConfirm(false)} className="p-2 rounded-full hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="bg-teal-50 border border-teal-100 rounded-xl p-3 text-teal-800 text-sm font-medium space-y-1">
                            {downloadScope?.submissionId || (downloadScope?.submissionIds && downloadScope.submissionIds.length) ? (
                                <>
                                    <div>Downloading {downloadScope.submissionIds?.length || 1} submission{(downloadScope.submissionIds?.length || 1) === 1 ? '' : 's'} from {downloadScope.brand || 'selected brand'}.</div>
                                    <div className="text-[11px] text-teal-700/80">Only the chosen submission folders will be included.</div>
                                </>
                            ) : downloadScope?.brand ? (
                                <>
                                    <div>Downloading brand: {downloadScope.brand}. All its submissions will be included (unless you picked specific ones).</div>
                                    <div className="text-[11px] text-teal-700/80">You can select submissions inside a brand to narrow it down.</div>
                                </>
                            ) : (
                                <>
                                    <div>{selectedImageBrands.size} brand folder{selectedImageBrands.size === 1 ? '' : 's'} selected. Each will contain submission subfolders.</div>
                                    <div className="text-[11px] text-teal-700/80">If you choose individual submissions in a brand, only those will download.</div>
                                </>
                            )}
                        </div>
                        <label className="flex items-start gap-3">
                            <input 
                                type="checkbox" 
                                checked={includeExcelWithImages} 
                                onChange={(e) => setIncludeExcelWithImages(e.target.checked)} 
                                className="mt-1"
                            />
                            <div>
                                <div className="text-sm font-bold text-zinc-800">Add Excel files into a "data" folder</div>
                                <div className="text-xs text-zinc-500">Creates one Excel per submission with item details.</div>
                            </div>
                        </label>
                    </div>
                    <div className="px-6 py-4 border-t border-zinc-100 bg-zinc-50 flex gap-3">
                        <button 
                            onClick={() => { setShowDownloadConfirm(false); setDownloadScope(null); }}
                            className="flex-1 py-3 bg-white border border-zinc-200 text-zinc-600 font-bold rounded-xl hover:bg-zinc-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={() => { handleDownloadImages(includeExcelWithImages, downloadScope || undefined); setShowDownloadConfirm(false); setDownloadScope(null); }}
                            className="flex-1 py-3 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-200 transition-all active:scale-95 flex items-center justify-center gap-2"
                        >
                            <Download className="w-4 h-4" /> Download
                        </button>
                    </div>
                </div>
            </div>
        )}

        <div>
            <h2 className="text-2xl font-bold text-zinc-900 tracking-tight">Export & Reports</h2>
            <p className="text-zinc-500 text-sm">Prepare and download formatted files for aggregators.</p>
        </div>

        <div className="bg-white border border-zinc-200 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-xs font-bold text-zinc-500 uppercase">Brand Filters</p>
                    <p className="text-[11px] text-zinc-400">Scope campaign options and exports.</p>
                </div>
                <button
                    className="text-xs font-bold text-teal-600 hover:text-teal-700"
                    onClick={() => setBrandFilter([])}
                >
                    Clear
                </button>
            </div>
            <div className="flex flex-wrap gap-2">
                {brands.map((b) => {
                    const checked = brandFilter.includes(b.id);
                    return (
                        <button
                            key={b.id}
                            onClick={() => {
                                const next = checked ? brandFilter.filter((id) => id !== b.id) : [...brandFilter, b.id];
                                setBrandFilter(next);
                            }}
                            className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${checked ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-white border-zinc-200 text-zinc-600 hover:border-teal-200 hover:text-teal-700'}`}
                        >
                            {b.name}
                        </button>
                    );
                })}
                {brands.length === 0 && <span className="text-xs text-zinc-500">No brands available.</span>}
            </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            
            {/* Control Panel */}
            <div className="lg:col-span-1 space-y-4">
                <div className="bg-white p-6 rounded-2xl border border-zinc-200 shadow-sm">
                    <h3 className="font-bold text-zinc-900 mb-4 text-sm uppercase tracking-wide">Export Settings</h3>
                    
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-zinc-500">Select Platform</label>
                            <select 
                                value={selectedAggregator}
                                onChange={(e) => setSelectedAggregator(e.target.value)}
                                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold text-zinc-700 focus:ring-2 focus:ring-teal-100 outline-none"
                            >
                                <option value="All Platforms">All Platforms</option>
                                {aggregators.filter(agg => agg.active).map(agg => <option key={agg.id} value={agg.name}>{agg.name}</option>)}
                            </select>
                        </div>
                         <div className="space-y-2">
                            <label className="text-xs font-bold text-zinc-500">Campaign</label>
                            <select 
                                value={selectedCampaignId}
                                onChange={(e) => setSelectedCampaignId(e.target.value)}
                                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-3 font-semibold text-zinc-700 focus:ring-2 focus:ring-teal-100 outline-none"
                            >
                                <option value={ALL_CAMPAIGNS}>All Campaigns</option>
                                {scopedCampaigns.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-zinc-100">
                        <button 
                            onClick={() => setShowExportModal(true)}
                            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-emerald-200 transition-all active:scale-95"
                        >
                            <FileSpreadsheet className="w-4 h-4" />
                            Download Excel
                        </button>
                         <button 
                            onClick={() => setShowImageModal(true)}
                            className="w-full mt-3 flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-teal-200 transition-all active:scale-95"
                        >
                            <ImageIcon className="w-4 h-4" />
                            Download Images
                        </button>
                        <button 
                            onClick={handleMarkExported}
                            disabled={isMarking}
                            className={`w-full mt-3 flex items-center justify-center gap-2 font-bold py-3 rounded-xl transition-all ${
                                isMarking
                                  ? 'bg-zinc-200 text-zinc-500 cursor-not-allowed'
                                  : 'bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-600'
                            }`}
                        >
                            <CheckCircle className="w-4 h-4" />
                            {isMarking ? 'Marking...' : 'Mark Exported'}
                        </button>
                    </div>
                </div>

                <div className="bg-teal-900 rounded-2xl p-6 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 opacity-10">
                        <FileSpreadsheet className="w-24 h-24" />
                    </div>
                    <h3 className="font-bold text-lg mb-2 relative z-10">Export Tips</h3>
                    <p className="text-teal-200 text-sm relative z-10 leading-relaxed">
                        Ensure all items are approved before exporting. 
                        {selectedAggregator === 'All Platforms' 
                            ? " You are viewing items from ALL platforms combined into one sheet."
                            : ` The generated Excel file follows the standard template for ${selectedAggregator}.`
                        }
                    </p>
                </div>
            </div>

            {/* Preview Panel */}
            <div className="lg:col-span-3">
                 <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden flex flex-col h-full">
                     <div className="px-6 py-4 border-b border-zinc-100 bg-zinc-50 flex justify-between items-center">
                         <h3 className="font-bold text-zinc-700 text-sm">Preview: {selectedAggregator} Export</h3>
                         <span className="text-xs font-bold text-zinc-400">Showing {previewItems.length} items</span>
                     </div>
                     <div className="overflow-x-auto">
        {previewItems.length === 0 ? (
            <div className="p-12 text-center text-zinc-400">
                No approved items found for this platform.
            </div>
        ) : (
            <table className="w-full text-xs text-left whitespace-nowrap">
                            <thead className="bg-white text-zinc-500 font-bold uppercase border-b border-zinc-100">
                                <tr>
                                    <th className="px-4 py-3">Image</th>
                                    <th className="px-4 py-3">Brand</th>
                                    <th className="px-4 py-3">Item Name</th>
                                    <th className="px-4 py-3">Original Price</th>
                                    <th className="px-4 py-3">Platforms</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-50">
                                {previewItems.map((item, i) => (
                                    <tr key={i} className="hover:bg-zinc-50/50">
                                        <td className="px-4 py-3">
                                            {resolveImageSrc((item as any).image || (item as any).imagePreview) ? (
                                                <div className="w-8 h-8 rounded-md border border-zinc-200 overflow-hidden bg-white">
                                                    <img src={resolveImageSrc((item as any).image || (item as any).imagePreview) as string} className="w-full h-full object-contain" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                                </div>
                                            ) : (
                                                <div className="w-8 h-8 rounded-md border border-zinc-100 bg-zinc-50 flex items-center justify-center text-zinc-300"><ImageIcon className="w-4 h-4" /></div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 font-bold text-zinc-800">{(item as any).brandLabel || (item as any).brand}</td>
                                        <td className="px-4 py-3 text-zinc-700">{item.name}</td>
                                        <td className="px-4 py-3 font-bold text-zinc-900">{item.sellingPrice}</td>
                                        <td className="px-4 py-3 text-zinc-400 max-w-[150px] truncate">{item.selectedAggregators.join(', ')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        )}
                     </div>
                 </div>
            </div>
        </div>
    </div>
  );
};
